// src/services/ServiceValidator.js
//
// Shared input validation utility for service-layer methods.
// Throws descriptive errors before any data reaches storage.
//
// Usage:
//   ServiceValidator.validate(data, schema, context);
//   ServiceValidator.requireString(value, fieldName, context);
//   ServiceValidator.requireNumber(value, fieldName, context);

class ServiceValidator {
  /**
   * Validate an object against a schema.
   *
   * Schema shape: { fieldName: { required?, type?, enum?, minLength?, min?, max? } }
   *
   * @param {*}      data    - Object to validate (must be a plain object)
   * @param {object} schema  - Validation rules per field
   * @param {string} context - Label used in error messages, e.g. 'GeoRiskService.addIndex'
   * @throws {Error} with a message that names the exact failing field(s)
   */
  static validate(data, schema, context = '') {
    const prefix = context ? `[${context}] ` : '';

    if (data === null || data === undefined || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error(
        `${prefix}Validation failed: expected an object, got ${
          data === null ? 'null' : Array.isArray(data) ? 'array' : typeof data
        }`
      );
    }

    const errors = [];

    for (const [field, rules] of Object.entries(schema)) {
      const value = data[field];
      const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '');

      if (rules.required && isEmpty) {
        errors.push(`'${field}' is required`);
        continue; // skip further checks when field is missing
      }

      if (isEmpty) continue; // optional field absent — nothing more to check

      if (rules.type && typeof value !== rules.type) {
        errors.push(
          `'${field}' must be of type ${rules.type}, got ${typeof value}`
        );
      }

      if (rules.enum && !rules.enum.includes(value)) {
        errors.push(
          `'${field}' must be one of [${rules.enum.join(', ')}], got '${value}'`
        );
      }

      if (
        rules.minLength !== undefined &&
        typeof value === 'string' &&
        value.trim().length < rules.minLength
      ) {
        errors.push(
          `'${field}' must be at least ${rules.minLength} character(s)`
        );
      }

      if (rules.min !== undefined && typeof value === 'number' && value < rules.min) {
        errors.push(`'${field}' must be >= ${rules.min}, got ${value}`);
      }

      if (rules.max !== undefined && typeof value === 'number' && value > rules.max) {
        errors.push(`'${field}' must be <= ${rules.max}, got ${value}`);
      }
    }

    if (errors.length > 0) {
      throw new Error(`${prefix}Validation failed: ${errors.join('; ')}`);
    }
  }

  /**
   * Require a scalar string value (not embedded in an object).
   * @param {*}      value    - Value to check
   * @param {string} field    - Field name for the error message
   * @param {string} context  - Context label
   */
  static requireString(value, field, context = '') {
    const prefix = context ? `[${context}] ` : '';
    if (value === null || value === undefined || typeof value !== 'string' || value.trim() === '') {
      throw new Error(
        `${prefix}Validation failed: '${field}' is required and must be a non-empty string, got ${
          value === null ? 'null' : value === undefined ? 'undefined' : `'${value}'`
        }`
      );
    }
  }

  /**
   * Require a finite number value.
   */
  static requireNumber(value, field, context = '') {
    const prefix = context ? `[${context}] ` : '';
    if (typeof value !== 'number' || !isFinite(value)) {
      throw new Error(
        `${prefix}Validation failed: '${field}' must be a finite number, got ${
          typeof value === 'number' ? value : `${typeof value} (${value})`
        }`
      );
    }
  }
}

export default ServiceValidator;
